import { useMemo } from "react";
import moment from "jalali-moment";
import { CroppingSuitabilityModel } from "@agroyaar/sdk";
import ReportPage from "@/shared/components/report-template/presentation/pages/ReportPage";
import Introduction from "@/shared/components/report-template/presentation/components/introduction/Introduction";
import CroppingSuitabilityDefinition from "@/features/reports/presentation/components/cropping-suitability-single-product/components/CroppingSuitabilityDefinition";
import FinalLandSuitabilityIndex from "@/features/reports/presentation/components/cropping-suitability-single-product/components/FinalLandSuitabilityIndex";
import HelpLandSuitabilityClass from "@/features/reports/presentation/components/cropping-suitability-single-product/components/HelpLandSuitabilityClass";
import AnnuallyPerformancePotential from "@/features/reports/presentation/components/cropping-suitability-single-product/components/AnnuallyPerformancePotential";
import DailyTemperatureChange from "@/features/reports/presentation/components/cropping-suitability-single-product/components/DailyTemperatureChange";
import AveragesGdd from "@/features/reports/presentation/components/cropping-suitability-single-product/components/AveragesGdd";
import TemperatureRisks from "@/features/reports/presentation/components/cropping-suitability-single-product/components/TemperatureRisks";
import HumidityRisks from "@/features/reports/presentation/components/cropping-suitability-single-product/components/HumidityRisks";
import IrrigationRequirement from "@/features/reports/presentation/components/cropping-suitability-single-product/components/IrrigationRequirement";

interface CroppingSuitabilityReportPageProps {
  isLoading: boolean;
  croppingSuitability: CroppingSuitabilityModel | undefined;
}

const CroppingSuitabilityReportPage = ({
  isLoading,
  croppingSuitability,
}: CroppingSuitabilityReportPageProps) => {
  const temperatureRisksChartPerPage = 2;
  const temperatureRisksPage = useMemo(
    () =>
      croppingSuitability?.reports &&
      Math.ceil(
        croppingSuitability?.reports?.length / temperatureRisksChartPerPage
      ),
    [croppingSuitability?.reports]
  );

  const chartYears = useMemo(
    () =>
      croppingSuitability?.reports[0]?.annuallyNetIrrigationRequirement?.map(
        ({ year }) => moment(`${year}-04-01`).format("jYYYY")
      ),
    [croppingSuitability?.reports]
  );

  return (
    <div>
      <ReportPage isLoading={isLoading}>
        {croppingSuitability && (
          <>
            <Introduction
              serviceType="CROPPING_SUITABILITY"
              farmer={croppingSuitability.farmer}
              farm={croppingSuitability.farm}
              irrigationSource={croppingSuitability.irrigationSource}
              introDes={
                <p className="text-[#222] text-[24px] !font-[B-Nazanin] mx-auto text-center">
                  ارزیابی{" "}
                  <span className="text-president text-[24px] !font-[B-Nazanin]">
                    {croppingSuitability?.reports[0]?.crop?.persianName}
                  </span>
                </p>
              }
            />

            <CroppingSuitabilityDefinition
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <FinalLandSuitabilityIndex
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <HelpLandSuitabilityClass />

            <AnnuallyPerformancePotential
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
            />

            <DailyTemperatureChange
              dailyMaximumTemperatureChange={
                croppingSuitability.dailyMaximumTemperatureChange
              }
              dailyMeanTemperatureChange={
                croppingSuitability.dailyMeanTemperatureChange
              }
              dailyMinimumTemperatureChange={
                croppingSuitability.dailyMinimumTemperatureChange
              }
            />

            <AveragesGdd
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
            />

            <TemperatureRisks
              croppingSuitabilityReports={croppingSuitability.reports}
              temperatureRisksPage={temperatureRisksPage!}
              temperatureRisksChartPerPage={temperatureRisksChartPerPage}
            />

            <HumidityRisks
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <IrrigationRequirement
              totalWaterSupplyHectare={
                croppingSuitability.totalWaterSupplyHectare
              }
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
            />
          </>
        )}
      </ReportPage>
    </div>
  );
};

export default CroppingSuitabilityReportPage;
